import {
	ChangeDetectionStrategy,
	Component,
	DestroyRef,
	ElementRef,
	HostBinding,
	inject,
	input,
	OnChanges,
	SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconRegistry } from '@angular/material/icon';
import { take, tap } from 'rxjs';

import { IconSize, IconVariation } from './icon.model';

@Component({
	selector: 'sl-icon',
	templateUrl: './icon.component.html',
	styleUrl: './icon.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
})
export class IconComponent implements OnChanges {
	readonly size = input<IconSize>(IconSize.Regular);
	readonly icon = input<string>();
	readonly variation = input<IconVariation>();
	readonly svgIcon = input<string>();

	#subs = null;

	readonly #matIconRegistry = inject(MatIconRegistry);
	readonly #destroyRef = inject(DestroyRef);
	readonly #elementRef = inject(ElementRef<HTMLElement>);

	@HostBinding('class') get classes(): Record<string, boolean> {
		return {
			'sl-icon': true,
			'material-symbols-outlined': true,
			[`sl-icon--${this.size()}`]: !!this.size(),
			[`sl-icon-variation--${this.variation()}`]: !!this.variation(),
		};
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.svgIcon && this.svgIcon()) {
			this.#subs?.unsubscribe();
			this.#subs = this.#matIconRegistry
				.getNamedSvgIcon(this.svgIcon())
				.pipe(
					takeUntilDestroyed(this.#destroyRef),
					take(1),
					tap(content => {
						this.#elementRef.nativeElement.innerHtml = '';
						this.#elementRef.nativeElement.appendChild(content);
					}),
				)
				.subscribe();
		}

		if (changes.icon && !this.svgIcon()) {
			this.#subs?.unsubscribe();
			this.#elementRef.nativeElement.innerHtml = '';
			this.#elementRef.nativeElement.innerText = this.icon();
		}
	}
}
